/**
 * Shared formatting helpers for eval results display.
 */

import { evaluatedRunCount, unprovenRunCount, type EvalRunCounts, type EvalRunOutcomeKind } from '@wayai/core/contracts';

interface ResultRow extends EvalRunCounts {
  eval_id: string;
  total_runs: number;
  // Non-terminal run counts. Present on the session-details payload; optional so
  // older/partial payloads still render (treated as zero in-progress).
  pending_runs?: number;
  running_runs?: number;
  avg_execution_time_ms?: number;
  aggregated_scores?: Record<string, { avg: number; min: number; max: number }>;
  eval?: { eval_id: string; eval_name: string } | null;
}

/**
 * The parenthetical that keeps a ratio honest: what is missing from the
 * denominator, and what has not finished. Shared by the per-eval rows and the
 * overall line so the two can't word it differently.
 */
function runNotes(unproven: number, inProgress: number): string[] {
  return [
    unproven > 0 ? `${unproven} unproven` : '',
    inProgress > 0 ? `${inProgress} in progress` : '',
  ].filter(Boolean);
}

/**
 * Print a results table and overall summary.
 *
 * @param mode - 'compact': scores show avg only (used by run-eval summary)
 *               'detailed': scores show avg/min/max (used by eval-results)
 */
export function printResultsTable(
  results: ResultRow[],
  mode: 'compact' | 'detailed'
): void {
  let totalPassed = 0;
  let totalEvaluated = 0;
  let totalUnproven = 0;
  let totalInProgress = 0;

  for (const r of results) {
    const evalName = r.eval?.eval_name || r.eval_id.slice(0, 8);
    // Denominator = runs that carry a verdict, matching `success_rate`.
    // Still-running runs would understate the rate mid-run (gh #2604), and runs
    // that errored or produced no usable verdict were never scored — both are
    // reported beside the ratio instead (gh #3756).
    const evaluatedRuns = evaluatedRunCount(r);
    const unproven = unprovenRunCount(r);
    const inProgress = (r.running_runs ?? 0) + (r.pending_runs ?? 0);
    const notes = runNotes(unproven, inProgress);
    const passed = notes.length > 0
      ? `${r.successful_runs}/${evaluatedRuns} passed (${notes.join(', ')})`
      : `${r.successful_runs}/${evaluatedRuns} passed`;
    const avgTime = r.avg_execution_time_ms != null ? `avg ${(r.avg_execution_time_ms / 1000).toFixed(1)}s` : '';

    let scoresStr = '';
    if (r.aggregated_scores) {
      const parts = Object.entries(r.aggregated_scores).map(([key, val]) =>
        mode === 'compact'
          ? `${key}: ${Math.round(val.avg * 100)}%`
          : `${key}: avg ${Math.round(val.avg * 100)}%, min ${Math.round(val.min * 100)}%, max ${Math.round(val.max * 100)}%`
      );
      if (parts.length > 0) scoresStr = '  ' + parts.join('  ');
    }

    console.log(`  ${evalName.padEnd(20)}  ${passed.padEnd(14)}  ${avgTime}${scoresStr}`);

    totalPassed += r.successful_runs;
    totalEvaluated += evaluatedRuns;
    totalUnproven += unproven;
    totalInProgress += inProgress;
  }

  const pct = totalEvaluated > 0 ? ((totalPassed / totalEvaluated) * 100).toFixed(1) : '0.0';
  // Unproven runs shrink the sample the percentage is computed over, so say so
  // rather than letting a "100%" over two surviving runs read as a clean sweep.
  const notes = runNotes(totalUnproven, totalInProgress);
  const note = notes.length > 0 ? ` — ${notes.join(', ')}` : '';
  console.log(`\nOverall: ${totalPassed}/${totalEvaluated} passed (${pct}%)${note}`);
}

/** Truncate a string to `maxLen`, appending an ellipsis when clipped. */
export function truncate(str: string, maxLen: number): string {
  if (str.length <= maxLen) return str;
  return str.slice(0, maxLen - 3) + '...';
}

/**
 * Shown when `--runs` (or a `run-eval` failure block) surfaces no evaluator
 * reasoning at all: the most common cause is a config gap — the message_evaluator
 * agent has no `notes` evaluation variable to write reasoning into — not a
 * missing feature (gh #2813). Wording tracks the skill's canonical vocabulary
 * (`evaluation_variables`), so a user who follows the hint finds the concept in
 * the docs.
 */
export const NO_EVALUATOR_NOTES_HINT =
  'No evaluator notes found — add a `notes` evaluation variable to the message_evaluator agent to surface its reasoning here.';

interface ToolCallView {
  function: { name: string; arguments: string };
}

interface ResponseView {
  content?: string | null;
  tool_calls?: ToolCallView[];
}

/** A run shape (loose subset) carrying the fields needed to explain a failure. */
export interface FailingRunView {
  run_number: number;
  response_match?: boolean | null;
  response_comment?: string | null;
  run_status?: string;
  /** Harness classification (gh #3756); absent on pre-taxonomy payloads. */
  outcome_kind?: EvalRunOutcomeKind;
  error_message?: string;
  eval_response?: ResponseView | null;
  expected_response?: ResponseView | null;
  eval?: { eval_name?: string | null } | null;
  eval_id?: string;
}

/**
 * One-line, truncated summary of a response: its text and/or tool-call
 * signatures. Shared between the actual (`eval_response`) and expected
 * (`expected_response`) sides so an expected-vs-actual diff reads symmetrically.
 */
export function summarizeResponse(resp: ResponseView | null | undefined, maxLen = 80): string {
  if (!resp) return '(none)';
  const parts: string[] = [];
  const content = typeof resp.content === 'string' ? resp.content.trim() : '';
  if (content) parts.push(`"${truncate(content, maxLen)}"`);
  if (resp.tool_calls && resp.tool_calls.length > 0) {
    parts.push(
      resp.tool_calls
        .map((tc) => `${tc.function.name}(${truncate(tc.function.arguments, maxLen)})`)
        .join(', '),
    );
  }
  return parts.length > 0 ? parts.join(' + ') : '(empty)';
}

/** Run statuses that mean "reached a verdict-bearing terminal state". */
const TERMINAL_RUN_STATUSES = new Set(['completed', 'failed']);

/**
 * A terminal run that didn't pass: an evaluator FAIL verdict, a run that
 * errored, or a completed run the evaluator returned no boolean verdict for —
 * the same set the aggregate's `failed_runs` counts, so this block explains
 * every run that field reports. An infrastructure death is exactly what an
 * operator needs the detail for, so it is listed and labeled by cause rather
 * than hidden. A still-running run (no terminal status yet) is never a failure.
 */
function isFailingRun(run: FailingRunView): boolean {
  if (run.run_status && !TERMINAL_RUN_STATUSES.has(run.run_status)) return false;
  return run.response_match !== true;
}

/** The bracket tag for a non-passing run — why it didn't pass, not just that it didn't. */
function failureTag(run: FailingRunView): string {
  switch (run.outcome_kind) {
    case 'execution_error': return 'ERROR';
    case 'evaluation_invalid': return 'INVALID';
    // `assertion_failure`, and any pre-taxonomy payload.
    default: return 'FAIL';
  }
}

/**
 * Print a "Failures" block explaining each red run inline — the evaluator's note
 * plus an expected-vs-actual diff — so a failing `run-eval` explains itself
 * without a second `eval-results --runs` (gh #2813). Closes with the no-notes
 * hint when failures exist but the evaluator emitted no reasoning. No-op when
 * nothing failed.
 */
export function printFailingRuns(runs: FailingRunView[]): void {
  const failing = runs.filter(isFailingRun);
  if (failing.length === 0) return;

  console.log(`\nFailures (${failing.length}):`);
  let anyComment = false;
  for (const run of failing) {
    const evalName = run.eval?.eval_name || run.eval_id?.slice(0, 8) || 'eval';
    console.log(`  ${evalName} — Run #${run.run_number} [${failureTag(run)}]`);
    if (run.error_message) {
      console.log(`    Error: ${run.error_message}`);
    }
    if (run.response_comment) {
      anyComment = true;
      console.log(`    Why: ${run.response_comment}`);
    }
    // Show the diff whenever an expected was captured; the actual is otherwise
    // invisible in run-eval's compact view.
    if (run.expected_response) {
      console.log(`    Expected: ${summarizeResponse(run.expected_response)}`);
      console.log(`    Actual:   ${summarizeResponse(run.eval_response)}`);
    }
  }

  if (!anyComment) {
    console.log(`\n${NO_EVALUATOR_NOTES_HINT}`);
  }
}
